/**
 * Koi Pond Ripples Visualizer — Calm maximalist dark pond with layered depth
 *
 * "Calm maximalism": visually rich, layered, immersive, sleep-safe.
 * 9 distinct visual layers with 3-plane parallax, 6-color palette with slow drift,
 * volumetric fog illusion from overlapping translucent blobs, hundreds of micro-specks,
 * caustic light network, and accent koi-glint arcs. All motion is slow; richness
 * comes from layering and micro-variation, not speed.
 *
 * Uses offscreen canvas trail technique for background controller compatibility.
 *
 * Rendering layers (bottom to top):
 * 1. Trail fade — offscreen canvas composited at reduced opacity
 * 2. Deep fog — large translucent radial blobs (back plane, 0.3× parallax)
 * 3. Caustic network — faint bezier curves connecting drifting control points
 * 4. Mid fog — medium translucent blobs (mid plane, 0.6× parallax)
 * 5. Ripples — concentric ring strokes, audio-driven via leaky integrator
 * 6. Micro specks — hundreds of tiny drifting dots with twinkle across all planes
 * 7. Koi glints — partial arc accent strokes with slow drift and fade
 * 8. Highlight fog — small bright blobs (front plane, 1.0× parallax)
 * 9. Vignette — radial gradient darkening edges for depth framing
 *
 * Audio mapping:
 * - Leaky integrator (EMA) on average energy — NOT beat detection
 * - Rising edge detection triggers 1–3 ripple spawns with configurable cooldown
 * - Bass subtly boosts fog blob radius and ripple expansion rate
 * - Treble modulates ring lineWidth shimmer and speck twinkle intensity
 * - Calmness raises threshold/cooldown for fewer, calmer spawns
 * - Ambient spawn ensures gentle ripples even in complete silence
 */

import { Base2DVisualizer } from '../lib/base-2d-visualizer.js';
import { VisualizerRegistry } from '../visualizer.js';
import { mapFrequencyData, smoothAmplitudes } from '../lib/audio-utils.js';

export const metadata = {
  name: 'koi-pond-ripples',
  displayName: 'Koi Pond Ripples',
  contextType: '2d',
};

// ─── Constants & Pool Capacities ──────────────────────────────────────────
const MAX_FOG = 120;
const MAX_SPECKS = 1200;
const MAX_RIPPLES = 40;
const MAX_GLINTS = 20;
const MAX_CAUSTIC_PTS = 24;
const TWO_PI = Math.PI * 2;
const DT = 1 / 60;

// 6 muted harmonious palettes (RGB) — teal → green → lavender → rose → gold → amber
const PALETTE = [
  [26, 107, 138],
  [42, 90, 58],
  [90, 74, 122],
  [107, 58, 90],
  [196, 163, 90],
  [122, 90, 42],
];

// Parallax depth-plane constants (back / mid / front)
const DEPTH_SPEED = [0.3, 0.6, 1.0];
const DEPTH_ALPHA = [0.025, 0.035, 0.05];
const DEPTH_RADIUS = [0.18, 0.11, 0.055];

class KoiPondRipples extends Base2DVisualizer {
  static name = 'koi-pond-ripples';
  static displayName = 'Koi Pond Ripples';

  static getConfigUI() {
    return [
      { type: 'section', label: 'Density' },
      { type: 'range', id: 'fogDensity', label: 'Fog Blobs', min: 20, max: 120, default: 65 },
      { type: 'range', id: 'speckDensity', label: 'Micro Specks', min: 100, max: 1200, default: 600 },
      { type: 'range', id: 'glintCount', label: 'Koi Glints', min: 4, max: 20, default: 12 },
      { type: 'range', id: 'paletteRichness', label: 'Palette Colors', min: 2, max: 6, default: 5 },

      { type: 'section', label: 'Ripples' },
      { type: 'range', id: 'rippleRate', label: 'Ripple Rate', min: 1, max: 100, default: 50, scale: 0.01 },
      { type: 'range', id: 'rippleLife', label: 'Ripple Life', min: 20, max: 200, default: 100, scale: 0.01 },
      { type: 'range', id: 'rippleSpeed', label: 'Ripple Speed', min: 10, max: 100, default: 40, scale: 0.01 },
      { type: 'range', id: 'ringThickness', label: 'Ring Thickness', min: 1, max: 10, default: 2.5 },

      { type: 'section', label: 'Effects' },
      { type: 'range', id: 'trailAlpha', label: 'Trail Fade', min: 2, max: 15, default: 4, scale: 0.01 },
      { type: 'range', id: 'driftSpeed', label: 'Drift Speed', min: 1, max: 30, default: 8, scale: 0.1 },
      { type: 'range', id: 'glowIntensity', label: 'Glow Intensity', min: 0, max: 20, default: 8 },
      { type: 'range', id: 'shimmerAmount', label: 'Shimmer', min: 0, max: 100, default: 40, scale: 0.01 },
      { type: 'range', id: 'vignetteStrength', label: 'Vignette', min: 0, max: 100, default: 45, scale: 0.01 },
      { type: 'range', id: 'brightness', label: 'Brightness', min: 20, max: 100, default: 60, scale: 0.01 },
      { type: 'range', id: 'calmness', label: 'Calmness', min: 0, max: 100, default: 40, scale: 0.01 },
      { type: 'range', id: 'smoothing', label: 'Audio Smoothing', min: 0, max: 95, default: 85, scale: 0.01 },

      { type: 'section', label: 'Safety' },
      { type: 'checkbox', id: 'safeMode', label: 'Safe Mode', default: false },
    ];
  }

  getDefaultConfig() { return {}; }
  getElementCount() { return 32; }

  init() {
    this.time = 0;
    this.isFirstFrame = true;
    this._paletteDrift = 0;

    this._trailCanvas = null;
    this._trailCtx = null;
    this._vignetteGradient = null;
    this._vignetteW = 0;
    this._vignetteH = 0;

    // Leaky integrator state
    this._smoothedEnergy = 0;
    this._prevSmoothedEnergy = 0;
    this._spawnCooldown = 0;
    this._ambientAccum = 0;

    // Reusable palette color output (zero-alloc)
    this._cr = 0;
    this._cg = 0;
    this._cb = 0;

    this._initFogPool();
    this._initSpeckPool();
    this._initRipplePool();
    this._initGlintPool();
    this._initCausticPool();
  }

  onConfigChange(changed) {
    if (changed.vignetteStrength !== undefined) {
      this._vignetteGradient = null;
    }
  }

  // ─── Pool Initializers ──────────────────────────────────────────────────

  _initFogPool() {
    this._fog = new Array(MAX_FOG);
    for (let i = 0; i < MAX_FOG; i++) {
      const t = i / MAX_FOG;
      const depth = t < 0.3 ? 0 : t < 0.75 ? 1 : 2;
      this._fog[i] = {
        cx: Math.random(), cy: Math.random(),
        freqX: 0.02 + Math.random() * 0.06,
        freqY: 0.015 + Math.random() * 0.05,
        ampX: 0.08 + Math.random() * 0.14,
        ampY: 0.06 + Math.random() * 0.12,
        phaseX: Math.random() * TWO_PI,
        phaseY: Math.random() * TWO_PI,
        baseRadius: 0.7 + Math.random() * 0.6,
        paletteT: Math.random(),
        depth,
      };
    }
  }

  _initSpeckPool() {
    this._specks = new Array(MAX_SPECKS);
    for (let i = 0; i < MAX_SPECKS; i++) {
      this._specks[i] = {
        x: Math.random(), y: Math.random(),
        vx: (Math.random() - 0.5) * 0.003,
        vy: (Math.random() - 0.5) * 0.002,
        size: 0.5 + Math.random() * 1.5,
        paletteT: Math.random(),
        depth: Math.floor(Math.random() * 3),
        twinklePhase: Math.random() * TWO_PI,
        twinkleFreq: 0.3 + Math.random() * 1.2,
        baseAlpha: 0.15 + Math.random() * 0.45,
      };
    }
  }

  _initRipplePool() {
    this._ripples = new Array(MAX_RIPPLES);
    for (let i = 0; i < MAX_RIPPLES; i++) {
      this._ripples[i] = {
        x: 0, y: 0, radius: 0, speed: 0,
        life: 0, maxLife: 0, phase: 0,
        intensity: 1, paletteT: 0, active: false,
      };
    }
    this._nextRipple = 0;
  }

  _initGlintPool() {
    this._glints = new Array(MAX_GLINTS);
    for (let i = 0; i < MAX_GLINTS; i++) {
      this._glints[i] = {
        x: 0, y: 0, angle: 0, arcLen: 0, radius: 0,
        driftVx: 0, driftVy: 0, life: 0, maxLife: 0,
        paletteT: 0, active: false,
      };
    }
    this._nextGlint = 0;
  }

  _initCausticPool() {
    this._causticPts = new Array(MAX_CAUSTIC_PTS);
    for (let i = 0; i < MAX_CAUSTIC_PTS; i++) {
      this._causticPts[i] = {
        cx: 0.1 + Math.random() * 0.8,
        cy: 0.1 + Math.random() * 0.8,
        freqX: 0.03 + Math.random() * 0.05,
        freqY: 0.02 + Math.random() * 0.04,
        phaseX: Math.random() * TWO_PI,
        phaseY: Math.random() * TWO_PI,
        ampX: 0.06 + Math.random() * 0.08,
        ampY: 0.05 + Math.random() * 0.07,
        _sx: 0, _sy: 0, // cached screen position
      };
    }
  }

  // ─── Helpers ────────────────────────────────────────────────────────────

  _ensureTrailCanvas() {
    const { canvas } = this;
    if (!this._trailCanvas || this._trailCanvas.width !== canvas.width || this._trailCanvas.height !== canvas.height) {
      if (typeof OffscreenCanvas !== 'undefined') {
        this._trailCanvas = new OffscreenCanvas(canvas.width, canvas.height);
      } else {
        this._trailCanvas = document.createElement('canvas');
        this._trailCanvas.width = canvas.width;
        this._trailCanvas.height = canvas.height;
      }
      this._trailCtx = this._trailCanvas.getContext('2d');
    }
  }

  /** Interpolate palette color at position t (0–1) with slow drift. Writes to _cr/_cg/_cb. */
  _paletteColor(t) {
    const richness = Math.max(2, Math.min(6, this.config.paletteRichness));
    const shifted = ((t + this._paletteDrift) % 1 + 1) % 1;
    const idx = shifted * (richness - 1);
    const i0 = Math.floor(idx);
    const i1 = Math.min(i0 + 1, richness - 1);
    const frac = idx - i0;
    const c0 = PALETTE[i0];
    const c1 = PALETTE[i1];
    this._cr = c0[0] + (c1[0] - c0[0]) * frac;
    this._cg = c0[1] + (c1[1] - c0[1]) * frac;
    this._cb = c0[2] + (c1[2] - c0[2]) * frac;
  }

  // ─── Spawning ───────────────────────────────────────────────────────────

  _spawnRipple(x, y, intensity) {
    const r = this._ripples[this._nextRipple];
    this._nextRipple = (this._nextRipple + 1) % MAX_RIPPLES;
    r.x = x; r.y = y; r.radius = 0;
    r.speed = (40 + Math.random() * 60) * this.config.rippleSpeed;
    r.life = (2.0 + Math.random() * 2.5) * this.config.rippleLife;
    r.maxLife = r.life;
    r.phase = Math.random() * TWO_PI;
    r.intensity = intensity;
    r.paletteT = Math.random();
    r.active = true;
  }

  _spawnGlint() {
    const { canvas } = this;
    const g = this._glints[this._nextGlint];
    this._nextGlint = (this._nextGlint + 1) % MAX_GLINTS;
    const drift = this.config.driftSpeed;
    g.x = 0.1 * canvas.width + Math.random() * 0.8 * canvas.width;
    g.y = 0.1 * canvas.height + Math.random() * 0.8 * canvas.height;
    g.angle = Math.random() * TWO_PI;
    g.arcLen = 0.4 + Math.random() * 1.0;
    g.radius = 12 + Math.random() * 35;
    g.driftVx = (Math.random() - 0.5) * drift * 20;
    g.driftVy = (Math.random() - 0.5) * drift * 20;
    g.life = 3.0 + Math.random() * 3.0;
    g.maxLife = g.life;
    g.paletteT = 0.6 + Math.random() * 0.4; // warm end
    g.active = true;
  }

  // ─── Draw Override (trail fade) ─────────────────────────────────────────

  draw(frequencyData, features = null) {
    const elementCount = this.getElementCount();
    if (!this._freqBuffer || this._freqBuffer.length !== elementCount) {
      this._freqBuffer = new Array(elementCount).fill(0);
    }
    const raw = mapFrequencyData(frequencyData, elementCount, this._freqBuffer);
    if (this.previousAmplitudes.length !== elementCount) {
      this.previousAmplitudes = new Array(elementCount).fill(0);
    }
    const amplitudes = smoothAmplitudes(raw, this.previousAmplitudes, this.config.smoothing);
    this._currentFeatures = features;
    const { ctx, canvas } = this;

    const trailAlpha = this.config.safeMode
      ? Math.min(this.config.trailAlpha, 0.03)
      : this.config.trailAlpha;

    if (this.isFirstFrame) {
      this.clearCanvas();
      this.isFirstFrame = false;
    } else {
      this._ensureTrailCanvas();
      this._trailCtx.clearRect(0, 0, canvas.width, canvas.height);
      this._trailCtx.drawImage(canvas, 0, 0);
      this.clearCanvas();
      ctx.save();
      ctx.globalAlpha = 1 - trailAlpha;
      ctx.drawImage(this._trailCanvas, 0, 0);
      ctx.restore();
    }
    this.renderFrame(amplitudes, features);
  }

  // ─── Main Render ────────────────────────────────────────────────────────

  renderFrame(amplitudes) {
    const { ctx, canvas, config } = this;
    if (canvas.width === 0 || canvas.height === 0) return;

    this.time += DT;
    this._paletteDrift = (this.time * 0.008) % 1; // full cycle ~125s

    const brightness = config.safeMode ? Math.min(config.brightness, 0.40) : config.brightness;
    const calmness = config.calmness;

    // ─── Audio: leaky integrator ───
    const alpha = 0.85 + calmness * 0.12;
    const threshold = 0.02 + calmness * 0.06;
    const cooldownDuration = 0.15 + calmness * 0.45;
    const len = amplitudes.length;

    let avgEnergy = 0, bassEnergy = 0, trebleEnergy = 0;
    for (let i = 0; i < len; i++) avgEnergy += amplitudes[i];
    avgEnergy /= len;

    const bassBins = Math.min(6, len);
    for (let i = 0; i < bassBins; i++) bassEnergy += amplitudes[i];
    bassEnergy /= bassBins;

    const trebleStart = Math.max(0, len - 8);
    for (let i = trebleStart; i < len; i++) trebleEnergy += amplitudes[i];
    trebleEnergy /= (len - trebleStart);

    this._prevSmoothedEnergy = this._smoothedEnergy;
    this._smoothedEnergy = this._smoothedEnergy * alpha + avgEnergy * (1 - alpha);

    // Ripple spawn on rising edge
    this._spawnCooldown = Math.max(0, this._spawnCooldown - DT);
    if (this._smoothedEnergy - this._prevSmoothedEnergy > threshold && this._spawnCooldown <= 0) {
      const count = 1 + Math.floor(config.rippleRate * 2);
      for (let i = 0; i < count; i++) {
        this._spawnRipple(
          0.15 * canvas.width + Math.random() * 0.7 * canvas.width,
          0.15 * canvas.height + Math.random() * 0.7 * canvas.height,
          0.5 + this._smoothedEnergy * 0.5
        );
      }
      this._spawnCooldown = cooldownDuration;
    }

    // Ambient ripple every ~3s
    this._ambientAccum += DT;
    if (this._ambientAccum >= 3.0) {
      this._ambientAccum = 0;
      this._spawnRipple(
        0.2 * canvas.width + Math.random() * 0.6 * canvas.width,
        0.2 * canvas.height + Math.random() * 0.6 * canvas.height, 0.25
      );
    }
    // Glint spawn ~every 2–4s
    if (Math.random() < DT * 0.4) this._spawnGlint();

    // ─── Draw all 8 content layers (layer 1 = trail, handled in draw()) ───
    this._drawFogLayer(ctx, canvas, 0, brightness, bassEnergy);   // L2: deep fog
    this._drawCaustics(ctx, canvas, brightness);                    // L3
    this._drawFogLayer(ctx, canvas, 1, brightness, bassEnergy);   // L4: mid fog
    this._drawRipples(ctx, canvas, brightness, bassEnergy, trebleEnergy); // L5
    this._drawSpecks(ctx, canvas, brightness, trebleEnergy);       // L6
    this._drawGlints(ctx, canvas, brightness);                     // L7
    this._drawFogLayer(ctx, canvas, 2, brightness, bassEnergy);   // L8: highlight fog
    this._drawVignette(ctx, canvas);                                // L9
  }

  // ─── L2/L4/L8: Fog Blobs (volumetric illusion via overlapping translucent radial gradients) ──

  _drawFogLayer(ctx, canvas, depthPlane, brightness, bassEnergy) {
    const w = canvas.width;
    const h = canvas.height;
    const t = this.time;
    const fogCount = Math.min(this.config.fogDensity, MAX_FOG);
    const speedMul = DEPTH_SPEED[depthPlane] * this.config.driftSpeed;
    const baseAlpha = DEPTH_ALPHA[depthPlane] * brightness;
    const baseRadius = DEPTH_RADIUS[depthPlane] * Math.min(w, h);
    const bassBoost = 1 + bassEnergy * 0.12;

    ctx.save();
    for (let i = 0; i < fogCount; i++) {
      const f = this._fog[i];
      if (f.depth !== depthPlane) continue;

      const sx = f.cx + Math.sin(t * f.freqX * speedMul + f.phaseX) * f.ampX;
      const sy = f.cy + Math.sin(t * f.freqY * speedMul + f.phaseY) * f.ampY;
      const px = sx * w;
      const py = sy * h;
      const r = baseRadius * f.baseRadius * bassBoost;

      this._paletteColor(f.paletteT);
      const cr = Math.round(this._cr);
      const cg = Math.round(this._cg);
      const cb = Math.round(this._cb);

      const grad = ctx.createRadialGradient(px, py, 0, px, py, r);
      grad.addColorStop(0, `rgba(${cr},${cg},${cb},${baseAlpha.toFixed(4)})`);
      grad.addColorStop(0.6, `rgba(${cr},${cg},${cb},${(baseAlpha * 0.4).toFixed(4)})`);
      grad.addColorStop(1, `rgba(${cr},${cg},${cb},0)`);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(px, py, r, 0, TWO_PI);
      ctx.fill();
    }
    ctx.restore();
  }

  // ─── L3: Caustic Network (faint underwater light lines) ─────────────────

  _drawCaustics(ctx, canvas, brightness) {
    const w = canvas.width;
    const h = canvas.height;
    const t = this.time;
    const pts = this._causticPts;
    const speed = this.config.driftSpeed * 0.5;

    for (let i = 0; i < MAX_CAUSTIC_PTS; i++) {
      const p = pts[i];
      p._sx = (p.cx + Math.sin(t * p.freqX * speed + p.phaseX) * p.ampX) * w;
      p._sy = (p.cy + Math.sin(t * p.freqY * speed + p.phaseY) * p.ampY) * h;
    }

    const threshSq = (0.14 * Math.min(w, h)) ** 2;
    const lineAlpha = 0.008 * brightness;
    if (lineAlpha < 0.002) return;

    ctx.save();
    ctx.lineWidth = 0.8;
    this._paletteColor(0.15);
    ctx.strokeStyle = `rgba(${Math.round(this._cr)},${Math.round(this._cg)},${Math.round(this._cb)},${lineAlpha.toFixed(4)})`;

    ctx.beginPath();
    for (let i = 0; i < MAX_CAUSTIC_PTS; i++) {
      const a = pts[i];
      for (let j = i + 1; j < MAX_CAUSTIC_PTS; j++) {
        const b = pts[j];
        const dx = b._sx - a._sx;
        const dy = b._sy - a._sy;
        if (dx * dx + dy * dy < threshSq) {
          const mx = (a._sx + b._sx) * 0.5 + dy * 0.08;
          const my = (a._sy + b._sy) * 0.5 - dx * 0.08;
          ctx.moveTo(a._sx, a._sy);
          ctx.quadraticCurveTo(mx, my, b._sx, b._sy);
        }
      }
    }
    ctx.stroke();
    ctx.restore();
  }

  // ─── L5: Ripples (concentric rings with palette color shift) ────────────

  _drawRipples(ctx, canvas, brightness, bassEnergy, trebleEnergy) {
    const { config } = this;
    const ringThickness = config.ringThickness;
    const shimmerAmount = config.shimmerAmount;
    const glowIntensity = config.glowIntensity;
    const useGlow = glowIntensity > 0;
    const minDim = Math.min(canvas.width, canvas.height);

    ctx.save();
    for (let i = 0; i < MAX_RIPPLES; i++) {
      const r = this._ripples[i];
      if (!r.active) continue;

      r.life -= DT;
      if (r.life <= 0) { r.active = false; continue; }

      r.radius += r.speed * DT * (1 + bassEnergy * 0.5);
      const lifeRatio = r.life / r.maxLife;
      const alphaFalloff = lifeRatio * lifeRatio;

      // Color shifts through palette as ripple expands
      this._paletteColor(r.paletteT + r.radius / (minDim * 0.5) * 0.3);
      const cr = Math.round(this._cr);
      const cg = Math.round(this._cg);
      const cb = Math.round(this._cb);
      const baseAlpha = alphaFalloff * r.intensity * brightness;

      for (let ring = 0; ring < 4; ring++) {
        const ringRadius = r.radius + ring * (ringThickness * 2.5 + 3);
        if (ringRadius <= 0) continue;
        const ringAlpha = baseAlpha * (1 - ring * 0.2);
        if (ringAlpha <= 0.005) continue;

        const shimmer = 1 + shimmerAmount * trebleEnergy *
          Math.sin(this.time * 8 + r.phase + ring * 1.2) * 0.5;
        const lw = Math.max(0.5, ringThickness * shimmer * (1 - ring * 0.15));

        ctx.beginPath();
        ctx.arc(r.x, r.y, ringRadius, 0, TWO_PI);
        ctx.strokeStyle = `rgba(${cr},${cg},${cb},${ringAlpha.toFixed(3)})`;
        ctx.lineWidth = lw;
        if (useGlow) {
          ctx.shadowBlur = glowIntensity * alphaFalloff;
          ctx.shadowColor = `rgba(${cr},${cg},${cb},${(ringAlpha * 0.5).toFixed(3)})`;
        }
        ctx.stroke();
      }
    }
    if (useGlow) ctx.shadowBlur = 0;
    ctx.restore();
  }

  // ─── L6: Micro Specks (tiny drifting dots with twinkle) ─────────────────

  _drawSpecks(ctx, canvas, brightness, trebleEnergy) {
    const w = canvas.width;
    const h = canvas.height;
    const count = Math.min(this.config.speckDensity, MAX_SPECKS);
    const t = this.time;

    ctx.save();
    for (let i = 0; i < count; i++) {
      const s = this._specks[i];

      // Parallax drift
      const speed = DEPTH_SPEED[s.depth];
      s.x += s.vx * speed;
      s.y += s.vy * speed;
      if (s.x < -0.02) s.x += 1.04;
      else if (s.x > 1.02) s.x -= 1.04;
      if (s.y < -0.02) s.y += 1.04;
      else if (s.y > 1.02) s.y -= 1.04;

      // Twinkle modulation (treble boosts twinkle intensity)
      const twinkle = 0.5 + 0.5 * Math.sin(t * s.twinkleFreq + s.twinklePhase);
      const alpha = s.baseAlpha * brightness * (0.4 + 0.6 * twinkle * (1 + trebleEnergy * 0.5));
      if (alpha < 0.01) continue;

      this._paletteColor(s.paletteT);
      ctx.fillStyle = `rgba(${Math.round(this._cr)},${Math.round(this._cg)},${Math.round(this._cb)},${alpha.toFixed(3)})`;
      ctx.fillRect(Math.round(s.x * w), Math.round(s.y * h), s.size, s.size);
    }
    ctx.restore();
  }

  // ─── L7: Koi Glints (accent arc strokes) ────────────────────────────────

  _drawGlints(ctx, canvas, brightness) {
    const glintCount = Math.min(this.config.glintCount, MAX_GLINTS);
    const driftSpeed = this.config.driftSpeed;

    ctx.save();
    for (let i = 0; i < glintCount; i++) {
      const g = this._glints[i];
      if (!g.active) continue;

      g.life -= DT;
      if (g.life <= 0) { g.active = false; continue; }

      g.x += g.driftVx * DT;
      g.y += g.driftVy * DT;
      g.angle += driftSpeed * 0.02 * DT;

      // Triangle envelope: fade in (first 20%), sustain, fade out (last 30%)
      const lifeRatio = g.life / g.maxLife;
      let envelope;
      if (lifeRatio > 0.8) envelope = (1 - lifeRatio) / 0.2;
      else if (lifeRatio < 0.3) envelope = lifeRatio / 0.3;
      else envelope = 1;

      const alpha = envelope * brightness * 0.5;
      if (alpha <= 0.005) continue;

      this._paletteColor(g.paletteT);
      ctx.beginPath();
      ctx.arc(g.x, g.y, g.radius, g.angle, g.angle + g.arcLen);
      ctx.strokeStyle = `rgba(${Math.round(this._cr)},${Math.round(this._cg)},${Math.round(this._cb)},${alpha.toFixed(3)})`;
      ctx.lineWidth = 2;
      ctx.stroke();
    }
    ctx.restore();
  }

  // ─── L9: Vignette (radial edge darkening) ──────────────────────────────

  _drawVignette(ctx, canvas) {
    const strength = this.config.vignetteStrength;
    if (strength <= 0) return;

    const w = canvas.width;
    const h = canvas.height;

    if (!this._vignetteGradient || this._vignetteW !== w || this._vignetteH !== h) {
      const cx = w * 0.5;
      const cy = h * 0.5;
      const r = Math.sqrt(cx * cx + cy * cy);
      this._vignetteGradient = ctx.createRadialGradient(cx, cy, r * 0.3, cx, cy, r);
      this._vignetteGradient.addColorStop(0, 'rgba(0,0,0,0)');
      this._vignetteGradient.addColorStop(1, 'rgba(0,0,0,1)');
      this._vignetteW = w;
      this._vignetteH = h;
    }

    ctx.save();
    ctx.globalAlpha = strength;
    ctx.fillStyle = this._vignetteGradient;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
  }

  // ─── Cleanup ────────────────────────────────────────────────────────────

  destroy() {
    this._trailCanvas = null;
    this._trailCtx = null;
    this._vignetteGradient = null;
    this._fog = null;
    this._specks = null;
    this._ripples = null;
    this._glints = null;
    this._causticPts = null;
    super.destroy();
  }
}

VisualizerRegistry.register(KoiPondRipples);
export default KoiPondRipples;
